import React, { useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { Button } from 'primereact/button';
import { useQuery } from 'react-query';
import { getProposalById } from 'apis/proposals';
import { getAllResearchFields } from 'apis/projectProposals.api';
import Loading from 'components/Loading';
import { catchError } from 'utils/func';
import ProposalForm from '../../components/Form';

export default function ViewProposalPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { id } = useParams();

  const {
    reset,
    control,
    watch,
    formState: { errors },
    setValue,
  } = useForm();

  const { data: proposalDetailData, isLoading } = useQuery(
    ['proposalDetail', id],
    () => getProposalById(id),
    {
      enabled: !!id,
      onError: (error) => {
        catchError(t, error, navigate);
      },
    }
  );

  const { data: researchFieldsData } = useQuery(
    ['projectProposalResearchFields'],
    getAllResearchFields
  );

  const researchFields = useMemo(
    () =>
      researchFieldsData?.data.map((e) => ({
        id: e.id,
        name: e.code ? t(`researchFields.${e.code}`) : e.name,
        code: e.code,
      })) ?? [],
    [researchFieldsData, t]
  );

  useEffect(() => {
    if (proposalDetailData?.data) {
      const data = proposalDetailData?.data;
      const owner = {
        ownerId: data?.ownerId,
        email: data?.owner?.email,
        contact: {
          email: data?.owner?.email,
        },
        fullname: data?.owner?.fullname,
        gender: data?.owner?.scientificProfile?.gender,
        academicRank: data?.owner?.scientificProfile?.academicRank,
        academicDegree: data?.owner?.scientificProfile?.academicDegree,
        organizationId: data?.owner?.organizationId,
        specificOrganizationName: data?.owner?.specificOrganizationName,
      };

      reset({
        vietnameseName: data.vietnameseName,
        description: data.description,
        yearProposed: data.yearProposed,
        target: data.target,
        type: data.type,
        projectType: data.projectType,
        researchFieldIds: data.proposalResearchFields.map(
          ({ researchField, researchFieldId }) => researchField?.id ?? researchFieldId
        ),
        file: data.attachmentFileUrl,
        owner,
        latestComment: data?.proposalRevises?.[0]?.note ?? '',
        proposalRevises: (data?.proposalRevises ?? []).map((item) => ({
          fullname: item.user.fullname,
          note: item.note,
          createdAt: item.createdAt,
          actionType: item.status,
        })),
      });
    }
  }, [proposalDetailData, setValue]);

  return (
    <>
      {isLoading && <Loading />}
      <div style={{ padding: '45.5px' }}>
        <form>
          <ProposalForm
            disabled
            control={control}
            errors={errors}
            researchFields={researchFields}
            proposalDetailData={proposalDetailData?.data}
            watch={watch}
            setValue={setValue}
          />
          <div
            className="text-right mt-4"
            style={{ display: 'flex', justifyContent: 'flex-end', gap: '14px' }}
          >
            <Button
              label={t('proposal.create.cancel')}
              className="w-7rem"
              severity="danger"
              onClick={() => navigate('/proposal')}
            />
          </div>
        </form>
      </div>
    </>
  );
}
